import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, ChevronDown, FileText, FileSpreadsheet, Sheet } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import { useTheme, money } from '../lib/theme';
import {
  getReportingInsights, getReport, reportExportUrl,
  getCategories, getExportSummary, exportCsvUrl, getReportingByBucket,
} from '../lib/api';
import BucketPill from '../components/BucketPill';

const REPORT_LIST = [
  { key: 'expense_summary',        label: 'Expense Summary' },
  { key: 'detailed_expenses',      label: 'Detailed Expenses' },
  { key: 'by_category',            label: 'By Category' },
  { key: 'by_vendor',              label: 'By Vendor' },
  { key: 'assets_capital_purchases', label: 'Assets & Capital Purchases' },
  { key: 'recurring_expenses',     label: 'Recurring Expenses' },
  { key: 'split_expenses',         label: 'Split Expenses' },
];

const PRESETS = [
  { key: 'all',     label: 'All time',     range: () => ({ start_date: '', end_date: '' }) },
  { key: 'this_month', label: 'This month', range: () => monthRange(new Date()) },
  { key: 'last_month', label: 'Last month', range: () => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); return monthRange(d);
  } },
  { key: 'this_quarter', label: 'This quarter', range: () => quarterRange(new Date()) },
  { key: 'last_quarter', label: 'Last quarter', range: () => {
    const d = new Date(); d.setMonth(d.getMonth() - 3); return quarterRange(d);
  } },
  { key: 'this_year', label: 'This year', range: () => yearRange(new Date().getFullYear()) },
  { key: 'last_year', label: 'Last year', range: () => yearRange(new Date().getFullYear() - 1) },
];

function fmt(d) { return d.toISOString().slice(0, 10); }
function monthRange(d) {
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { start_date: fmt(start), end_date: fmt(end) };
}
function quarterRange(d) {
  const q = Math.floor(d.getMonth() / 3);
  const start = new Date(d.getFullYear(), q * 3, 1);
  const end = new Date(d.getFullYear(), q * 3 + 3, 0);
  return { start_date: fmt(start), end_date: fmt(end) };
}
function yearRange(y) {
  return { start_date: `${y}-01-01`, end_date: `${y}-12-31` };
}

export default function Reporting() {
  const navigate = useNavigate();
  const { colors, effective } = useTheme();

  // Filters
  const [status, setStatus] = useState('approved');
  const [preset, setPreset] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expenseType, setExpenseType] = useState('all');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);

  // Data
  const [insights, setInsights] = useState(null);
  const [byBucket, setByBucket] = useState(null);
  const [selectedReport, setSelectedReport] = useState('expense_summary');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    getCategories()
      .then((r) => setCategories(r.categories.filter((c) => !c.is_special)))
      .catch(() => setCategories([]));
  }, []);

  const filters = useMemo(() => ({
    status: status === 'all' ? 'all' : status,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    categories: category ? [category] : undefined,
    expense_type: expenseType,
  }), [status, startDate, endDate, category, expenseType]);

  const applyPreset = useCallback((p) => {
    setPreset(p.key);
    const r = p.range();
    setStartDate(r.start_date);
    setEndDate(r.end_date);
  }, []);

  // Load insights + Send-to-QuickBooks summary + by-bucket
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [i, s, bb] = await Promise.all([
          getReportingInsights(filters),
          getExportSummary({ status: filters.status, start_date: filters.start_date, end_date: filters.end_date }).catch(() => null),
          getReportingByBucket({
            status: filters.status,
            start_date: filters.start_date, end_date: filters.end_date,
            expense_type: filters.expense_type,
          }).catch(() => null),
        ]);
        if (!cancelled) { setInsights(i); setSummary(s); setByBucket(bb); }
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [filters]);

  // Load current report
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getReport(selectedReport, { ...filters, limit: 100 });
        if (!cancelled) setReportData(d);
      } catch (e) { console.error(e); }
    })();
    return () => { cancelled = true; };
  }, [selectedReport, filters]);

  const monthlyData = insights?.monthly_trend?.map((m) => ({ ...m, label: m.month })) ?? [];
  const catData = (insights?.category_breakdown ?? []).slice(0, 5);
  const catOther = (insights?.category_breakdown ?? []).slice(5);
  const catChart = [
    ...catData,
    ...(catOther.length ? [{ category: 'Other', total: catOther.reduce((s, c) => s + c.total, 0) }] : []),
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="reporting-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="reporting-back" onClick={() => navigate(-1)}
          className="p-1 hover:opacity-70" style={{ color: colors.text }}>
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: colors.text }}>Reporting</h1>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl border p-4 mb-6"
        data-testid="reporting-filters"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <div className="flex flex-wrap gap-2 mb-3">
          {PRESETS.map((p) => (
            <button key={p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => applyPreset(p)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                backgroundColor: preset === p.key ? colors.accentSoft : colors.surface,
                borderColor: preset === p.key ? colors.accent : colors.border,
                color: preset === p.key ? colors.accent : colors.text,
              }}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <FilterInput label="Start" type="date" value={startDate}
            onChange={(v) => { setStartDate(v); setPreset(''); }} colors={colors} testID="filter-start" />
          <FilterInput label="End" type="date" value={endDate}
            onChange={(v) => { setEndDate(v); setPreset(''); }} colors={colors} testID="filter-end" />
          <FilterSelect label="Status" value={status} onChange={setStatus} colors={colors} testID="filter-status"
            options={[['approved','Approved'], ['pending','Pending'], ['rejected','Rejected'], ['all','All']]} />
          <FilterSelect label="Type" value={expenseType} onChange={setExpenseType} colors={colors} testID="filter-type"
            options={[['all','All'], ['expenses','Expenses only'], ['assets','Assets only']]} />
          <FilterSelect label="Category" value={category} onChange={setCategory} colors={colors} testID="filter-category"
            options={[['','All'], ...categories.map((c) => [c.name, c.name])]} />
        </div>
      </div>

      {/* Phase 21 — By Account Type card (QuickBooks-aligned buckets) */}
      {byBucket?.rows?.length ? (
        <div className="rounded-xl border p-4 mb-6" data-testid="by-bucket-card"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                By Account Type
              </p>
              <p className="text-sm mt-0.5" style={{ color: colors.text }}>
                QuickBooks-aligned buckets · Grand total <span className="tabular-nums font-semibold">{money(byBucket.grand_total || 0)}</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {byBucket.rows.map((r) => {
              const zero = (r.total || 0) <= 0;
              return (
                <div key={r.bucket} data-testid={`bucket-row-${r.bucket}`}
                  className="rounded-lg p-3"
                  style={{
                    backgroundColor: zero ? colors.bg : `${r.color}12`,
                    border: `1px solid ${zero ? colors.border : `${r.color}55`}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <BucketPill bucket={r.bucket} catalog={byBucket.rows.map((row) => ({ code: row.bucket, label: row.label, color: row.color, order: row.order }))} />
                    <span className="text-[10px] font-semibold tabular-nums" style={{ color: colors.textMuted }}>
                      {r.pct?.toFixed?.(1) ?? '0.0'}%
                    </span>
                  </div>
                  <p className="text-lg font-bold tabular-nums" style={{ color: colors.text }}>
                    {money(r.total)}
                  </p>
                  <p className="text-[11px]" style={{ color: colors.textMuted }}>
                    {r.count} receipt{r.count === 1 ? '' : 's'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Business Insights */}
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>
        Business Insights
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="insights-grid">
        <Card title="Monthly Trend" colors={colors}>
          {monthlyData.length === 0 ? (
            <EmptyCell colors={colors} />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis dataKey="label" fontSize={10} stroke={colors.textMuted} />
                <YAxis fontSize={10} stroke={colors.textMuted} />
                <Tooltip contentStyle={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, fontSize: 12 }}
                  formatter={(v) => money(v)} />
                <Line type="monotone" dataKey="total" stroke={colors.accent} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="By Category (top 5 + Other)" colors={colors}>
          {catChart.length === 0 ? (
            <EmptyCell colors={colors} />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={catChart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis type="number" fontSize={10} stroke={colors.textMuted} />
                <YAxis dataKey="category" type="category" width={100} fontSize={10} stroke={colors.textMuted} />
                <Tooltip contentStyle={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, fontSize: 12 }}
                  formatter={(v) => money(v)} />
                <Bar dataKey="total" fill={colors.accent} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Top Vendors" colors={colors}>
          <div className="divide-y" style={{ borderColor: colors.border }}>
            {(insights?.top_vendors ?? []).slice(0, 8).map((v, i) => (
              <div key={v.vendor} className="flex items-center justify-between py-2 text-sm">
                <span style={{ color: colors.textMuted }}>{i + 1}.</span>
                <span className="flex-1 mx-2 truncate" style={{ color: colors.text }}>{v.vendor}</span>
                <span className="tabular-nums" style={{ color: colors.text }}>{money(v.total)}</span>
              </div>
            ))}
            {(insights?.top_vendors ?? []).length === 0 ? <EmptyCell colors={colors} /> : null}
          </div>
        </Card>
        <Card title="Largest Expenses" colors={colors}>
          <div className="divide-y" style={{ borderColor: colors.border }}>
            {(insights?.largest_expenses ?? []).slice(0, 5).map((r) => (
              <button key={r.id} onClick={() => navigate(`/receipts/${r.id}`)}
                className="w-full flex items-center justify-between py-2 text-sm text-left hover:opacity-80">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate" style={{ color: colors.text }}>{r.vendor || 'Unknown'}</p>
                  <p className="text-xs" style={{ color: colors.textMuted }}>{r.date} · {r.category || '—'}</p>
                </div>
                <span className="tabular-nums font-semibold" style={{ color: colors.text }}>{money(r.total)}</span>
              </button>
            ))}
            {(insights?.largest_expenses ?? []).length === 0 ? <EmptyCell colors={colors} /> : null}
          </div>
        </Card>
      </div>

      {/* Reports */}
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>
        Expense Reports
      </p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="report-tabs">
        {REPORT_LIST.map((r) => (
          <button key={r.key}
            data-testid={`report-tab-${r.key}`}
            onClick={() => setSelectedReport(r.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
            style={{
              backgroundColor: selectedReport === r.key ? colors.accentSoft : colors.surface,
              borderColor: selectedReport === r.key ? colors.accent : colors.border,
              color: selectedReport === r.key ? colors.accent : colors.text,
            }}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border overflow-hidden mb-6"
        data-testid="report-container"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: colors.border }}>
          <h3 className="text-sm font-bold" style={{ color: colors.text }}>
            {REPORT_LIST.find((r) => r.key === selectedReport)?.label}
          </h3>
          <ExportMenu type={selectedReport} filters={filters} colors={colors} />
        </div>
        <ReportView report={reportData} colors={colors} type={selectedReport} navigate={navigate} />
      </div>

      {/* Legacy QuickBooks CSV (unchanged endpoint) */}
      <div className="rounded-xl border p-4"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>
          Send to QuickBooks
        </p>
        <p className="text-sm mb-3" style={{ color: colors.text }}>
          {summary ? `${summary.count} receipt${summary.count === 1 ? '' : 's'} · Total ${money(summary.total || 0)}` : '—'}
        </p>
        <a
          href={exportCsvUrl({
            status: status === 'all' ? 'approved' : status,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            category: category || undefined,
          })}
          data-testid="qb-legacy-csv"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: colors.accent }}>
          <Download size={14} /> Download QuickBooks CSV
        </a>
      </div>
    </div>
  );
}

// ---- helpers ----
function Card({ title, colors, children }) {
  return (
    <div className="rounded-xl border p-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      <p className="text-xs font-semibold mb-3" style={{ color: colors.textMuted }}>{title}</p>
      {children}
    </div>
  );
}

function EmptyCell({ colors }) {
  return <p className="text-xs py-8 text-center" style={{ color: colors.textMuted }}>No data</p>;
}

function FilterInput({ label, type = 'text', value, onChange, colors, testID }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: colors.textMuted }}>{label}</label>
      <input data-testid={testID} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 rounded-lg text-xs outline-none border"
        style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }} />
    </div>
  );
}
function FilterSelect({ label, value, onChange, options, colors, testID }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: colors.textMuted }}>{label}</label>
      <select data-testid={testID} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 rounded-lg text-xs outline-none border"
        style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function ExportMenu({ type, filters, colors }) {
  const [open, setOpen] = useState(false);
  const item = (fmt, Icon, label) => (
    <a
      href={reportExportUrl(type, fmt, filters)}
      data-testid={`export-${type}-${fmt}`}
      className="flex items-center gap-2 px-3 py-2 text-sm hover:opacity-80"
      style={{ color: colors.text }}
      onClick={() => setOpen(false)}>
      <Icon size={14} style={{ color: colors.textMuted }} />
      {label}
    </a>
  );
  return (
    <div className="relative">
      <button
        data-testid={`export-menu-${type}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border"
        style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}>
        <Download size={12} /> Export <ChevronDown size={12} />
      </button>
      {open ? (
        <div className="absolute right-0 mt-1 rounded-lg border shadow-lg z-10 min-w-[140px]"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
          {item('csv',  Sheet,          'CSV')}
          {item('xlsx', FileSpreadsheet, 'Excel')}
          {item('pdf',  FileText,        'PDF')}
        </div>
      ) : null}
    </div>
  );
}

function ReportView({ report, colors, type, navigate }) {
  if (!report) {
    return <p className="text-xs p-6 text-center" style={{ color: colors.textMuted }}>Loading…</p>;
  }

  if (type === 'expense_summary') {
    const s = report.summary;
    const cells = [
      ['Total Amount', money(s.total_amount)],
      ['Receipt Count', s.receipt_count],
      ['Average Receipt', money(s.avg_receipt)],
      ['Total GST', money(s.total_gst)],
      ['Total QST', money(s.total_qst)],
      ['Total HST', money(s.total_hst)],
      ['Expected Tax (engine)', money(s.total_expected_tax || 0)],
      ['Actual Tax (engine)', money(s.total_actual_tax || 0)],
      ['Total Discrepancy', money(s.total_discrepancy || 0)],
      ['Taxable Subtotal', money(s.taxable_subtotal || 0)],
      ['Zero-rated / Exempt', s.zero_rated_or_exempt_count || 0],
    ];
    return (
      <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-3">
        {cells.map(([k, v]) => (
          <div key={k} className="rounded-lg p-3" style={{ backgroundColor: colors.bg }} data-testid={`summary-${k.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
            <p className="text-[10px] font-bold uppercase" style={{ color: colors.textMuted }}>{k}</p>
            <p className="text-xl font-bold tabular-nums mt-1" style={{ color: colors.text }}>{v}</p>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'by_category' || type === 'by_vendor') {
    const rows = report.groups || [];
    const isCat = type === 'by_category';
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead>
            <tr style={{ backgroundColor: colors.bg, color: colors.textMuted }}>
              <th className="text-left px-4 py-2 text-xs font-bold uppercase">{isCat ? 'Category' : 'Vendor'}</th>
              <th className="text-right px-4 py-2 text-xs font-bold uppercase">Total</th>
              <th className="text-right px-4 py-2 text-xs font-bold uppercase">Count</th>
              {isCat ? <><th className="text-right px-4 py-2 text-xs font-bold uppercase">GST</th><th className="text-right px-4 py-2 text-xs font-bold uppercase">QST</th></> : <><th className="text-left px-4 py-2 text-xs font-bold uppercase">First</th><th className="text-left px-4 py-2 text-xs font-bold uppercase">Last</th></>}
            </tr>
          </thead>
          <tbody>
            {rows.map((g, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td className="px-4 py-2 truncate" style={{ color: colors.text }}>{isCat ? g.category : g.vendor}</td>
                <td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.text }}>{money(g.total)}</td>
                <td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.textMuted }}>{g.count}</td>
                {isCat ? <><td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.textMuted }}>{money(g.gst)}</td><td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.textMuted }}>{money(g.qst)}</td></>
                       : <><td className="px-4 py-2 text-xs" style={{ color: colors.textMuted }}>{g.first_seen || '—'}</td><td className="px-4 py-2 text-xs" style={{ color: colors.textMuted }}>{g.last_seen || '—'}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'recurring_expenses') {
    const rows = report.vendors || [];
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: colors.bg, color: colors.textMuted }}>
              <th className="text-left px-4 py-2 text-xs font-bold uppercase">Vendor</th>
              <th className="text-right px-4 py-2 text-xs font-bold uppercase">Occurrences</th>
              <th className="text-right px-4 py-2 text-xs font-bold uppercase">Total</th>
              <th className="text-right px-4 py-2 text-xs font-bold uppercase">Avg</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((v, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td className="px-4 py-2 truncate" style={{ color: colors.text }}>{v.vendor}</td>
                <td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.text }}>{v.occurrence_count}</td>
                <td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.text }}>{money(v.total_spent)}</td>
                <td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.textMuted }}>{money(v.avg_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // detailed_expenses / assets_capital_purchases / split_expenses — receipt-oriented lists
  const items = report.items || [];
  const isDetailedTax = type === 'detailed_expenses';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={isDetailedTax ? 'detailed-expenses-table' : undefined}>
        <thead>
          <tr style={{ backgroundColor: colors.bg, color: colors.textMuted }}>
            <th className="text-left px-4 py-2 text-xs font-bold uppercase">Date</th>
            <th className="text-left px-4 py-2 text-xs font-bold uppercase">Vendor</th>
            <th className="text-left px-4 py-2 text-xs font-bold uppercase">Category</th>
            {isDetailedTax ? (
              <>
                <th className="text-right px-4 py-2 text-xs font-bold uppercase">Expected Tax</th>
                <th className="text-right px-4 py-2 text-xs font-bold uppercase">Actual Tax</th>
                <th className="text-right px-4 py-2 text-xs font-bold uppercase">Discrepancy</th>
              </>
            ) : null}
            <th className="text-right px-4 py-2 text-xs font-bold uppercase">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const vendor = typeof r.vendor === 'object' ? r.vendor.value : r.vendor;
            const total = typeof r.total === 'object' ? r.total.value : r.total;
            const dateVal = typeof r.date === 'object' && r.date !== null ? r.date.value : r.date;
            const disc = Number(r.tax_discrepancy || 0);
            const discTone = Math.abs(disc) < 0.01
              ? colors.textMuted
              : (Math.abs(disc) > 5 ? (colors.error || '#EF4444') : (colors.warning || '#F59E0B'));
            return (
              <tr key={r.id}
                onClick={() => navigate(`/receipts/${r.id}`)}
                className="cursor-pointer hover:opacity-80"
                style={{ borderTop: `1px solid ${colors.border}` }}>
                <td className="px-4 py-2 text-xs" style={{ color: colors.textMuted }}>{dateVal || '—'}</td>
                <td className="px-4 py-2 truncate" style={{ color: colors.text }}>{vendor || 'Unknown'}</td>
                <td className="px-4 py-2 text-xs" style={{ color: colors.textMuted }}>{r.category || '—'}</td>
                {isDetailedTax ? (
                  <>
                    <td className="px-4 py-2 text-right tabular-nums text-xs" style={{ color: colors.textMuted }}>
                      {money(Number(r.expected_tax || 0))}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs" style={{ color: colors.textMuted }}>
                      {money(Number(r.actual_tax || 0))}
                    </td>
                    <td
                      data-testid={`disc-cell-${r.id}`}
                      className="px-4 py-2 text-right tabular-nums text-xs font-semibold"
                      style={{ color: discTone }}>
                      {disc >= 0 ? '+' : ''}{money(disc)}
                    </td>
                  </>
                ) : null}
                <td className="px-4 py-2 text-right tabular-nums" style={{ color: colors.text }}>{money(total || 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {items.length === 0 ? <p className="p-6 text-xs text-center" style={{ color: colors.textMuted }}>No data</p> : null}
    </div>
  );
}
